import { useCallback, useEffect, useMemo, useState } from "react";
import TanstackDatagrid from "components/ReusableComponents/TanstackDatagrid/TanstackDatagrid";
import { Box, Button, Chip, Divider, Stack, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import axios from "axios";
import { GlobalState } from "context/GlobalContext";
import { parseErrorMessage } from "utils/ErrorFunctions/parseErrorMessage";
import { AddRounded, DownloadRounded, FilterAltRounded, Publish, Unpublished } from "@mui/icons-material";
import CreateModal from "./CreateModal/CreateModal";
import {
	TRIMS_VIEW_LEVELS,
	trimCategoryColumn,
	trimsMasterColumnsObj,
	trimsSampleColumnsObj,
	trimsSupplierColumnsObj,
	trimsViewLevelOptions,
	isTrimSupplierBlocked,
	TRIM_PUBLISH_STATUS,
	getTrimPublishStatusDisplay,
	isTrimPublished,
	lsTrimsFilterValStr,
	getTrimsMasterFilterColumns,
	getTrimsSupplierFilterColumns,
	getAllTrimsFilterColumnDefs,
	isValidTrimImageUrl,
} from "./data";
import { AuthState } from "context/AuthContext";
import { createdByColumns, publishColumns, statusOptions } from "data/globalData";
import moment from "moment";
import { grey } from "@mui/material/colors";
import UpdateModal from "./UpdateModal/UpdateModal";
import PublishModal from "./PublishModal/PublishModal";
import DataExportModal from "./DataExportModal/DataExportModal";
import FilterModal from "./FilterModal/FilterModal";
import { custom, departments } from "data/authData";
import { allowAccess } from "utils/userAccessFun";

const normalizeTrimStatusPayload = (payload = {}) => ({
	...payload,
	status: payload?.status ?? statusOptions.CREATED.key,
	trim_active_status: payload?.trim_active_status ?? "INACTIVE",
	published_by: payload?.published_by ?? "",
	// DATE column: never send "" — MySQL/Sequelize rejects empty string for DATEONLY
	published_date: payload?.published_date || null,
});

const KpiChip = ({ label, value, color }) => (
	<Box
		sx={{
			px: 1.25,
			py: 0.4,
			borderRadius: 1.5,
			border: `1px solid ${grey[200]}`,
			bgcolor: "#fff",
			minWidth: 72,
			flexShrink: 0,
			textAlign: "center",
		}}>
		<Typography variant="body2" color="text.secondary" display="block" noWrap lineHeight={1.25} fontSize={13}>
			{label}
		</Typography>
		<Typography variant="body1" fontWeight={700} color={color} lineHeight={1.35} fontSize={15}>
			{value ?? "-"}
		</Typography>
	</Box>
);

const publishStatusBadgeCell = (status) => {
	const display = getTrimPublishStatusDisplay(status);
	return (
		<Box width="100%" display="flex" justifyContent="center">
			<Box
				sx={{
					py: 0.5,
					px: 1,
					borderRadius: 1,
					fontWeight: 700,
					fontSize: 14,
					textAlign: "center",
					color: display.color,
					bgcolor: display.bgcolor,
					width: "fit-content",
				}}>
				{display.label}
			</Box>
		</Box>
	);
};

const statusBadgeCell = (value, activeValue = "ACTIVE") => (
	<Box width="100%" display="flex" justifyContent="center">
		<Box
			sx={{
				py: 0.5,
				px: 1,
				borderRadius: 1,
				fontWeight: 700,
				fontSize: 14,
				textAlign: "center",
				color: value === activeValue ? "#2e7d32" : "#b71c1c",
				bgcolor: value === activeValue ? "#e8f5e9" : "#ffebee",
				width: "fit-content",
			}}>
			{value || "-"}
		</Box>
	</Box>
);

const TrimsMaster = () => {
	const { setLoadingOpen, snackBar, setSnackBarOpen, setSnackBar } = GlobalState();
	const { user } = AuthState();
	const blockSupplierDetails = isTrimSupplierBlocked(user);
	const trimViewOnly = blockSupplierDetails;
	const [data, setData] = useState([]);
	const [rowCount, setRowCount] = useState(0);
	const [kpi, setKpi] = useState({});
	const [filterModal, setFilterModal] = useState(false);
	const [filterColumns, setFilterColumns] = useState(() => {
		if (!localStorage.getItem(lsTrimsFilterValStr)) return [];
		const localStorageObj = JSON.parse(localStorage.getItem(lsTrimsFilterValStr));
		if (typeof localStorageObj !== "object" || !localStorageObj) return [];
		return getAllTrimsFilterColumnDefs().filter((row) => localStorageObj[row.key] !== undefined);
	});
	const [filterColumnVal, setFilterColumnVal] = useState(() => {
		if (localStorage.getItem(lsTrimsFilterValStr)) {
			return JSON.parse(localStorage.getItem(lsTrimsFilterValStr));
		}
		return {};
	});
	const [viewLevel, setViewLevel] = useState(TRIMS_VIEW_LEVELS.MASTER);
	const [modal, setModal] = useState(false);
	const [modalMode, setModalMode] = useState(false);
	const [selectedRow, setSelectedRow] = useState(false);
	const [publishModal, setPublishModal] = useState(false);
	const [publishModalMode, setPublishModalMode] = useState("publish");
	const [selectedPublishRow, setSelectedPublishRow] = useState(null);
	const [exportModal, setExportModal] = useState(false);

	useEffect(() => {
		if (!blockSupplierDetails || viewLevel !== TRIMS_VIEW_LEVELS.SUPPLIER) return;
		setViewLevel(TRIMS_VIEW_LEVELS.MASTER);
		setData([]);
		setRowCount(0);
		setKpi({});
	}, [blockSupplierDetails, viewLevel]);

	const availableViewLevelOptions = useMemo(
		() =>
			blockSupplierDetails
				? trimsViewLevelOptions.filter((option) => option.key === TRIMS_VIEW_LEVELS.MASTER)
				: trimsViewLevelOptions,
		[blockSupplierDetails],
	);

	const filterColumnOptions = useMemo(
		() => (viewLevel === TRIMS_VIEW_LEVELS.SUPPLIER ? getTrimsSupplierFilterColumns() : getTrimsMasterFilterColumns()),
		[viewLevel],
	);

	const handleFilterClick = () => {
		setFilterModal(true);
	};

	const handleCloseFilterModal = () => {
		setFilterModal(false);
	};

	const trimsImageColumn = {
		key: "url",
		name: "Image",
		minWidth: 96,
		maxWidth: 96,
		width: 96,
		align: "center",
		headerAlign: "center",
		renderCell: ({ row }) => (
			<Box width="100%" height="100%" display="flex" justifyContent="center" alignItems="center" py={0.5}>
				<Box
					width="45px"
					height="55px"
					border={`1px solid ${grey[200]}`}
					borderRadius={1}
					overflow="hidden"
					position="relative"
					bgcolor={grey[50]}>
					{isValidTrimImageUrl(row?.url) ? (
						<img
							src={row.url}
							alt={row?.trim_code || "trim"}
							style={{
								width: "105%",
								position: "absolute",
								left: "50%",
								top: "50%",
								transform: "translate(-50%, -50%)",
							}}
						/>
					) : (
						<Box
							width="100%"
							height="100%"
							display="flex"
							alignItems="center"
							justifyContent="center"
							color={grey[500]}
							fontSize={11}>
							No Image
						</Box>
					)}
				</Box>
			</Box>
		),
	};

	const canManagePublish = allowAccess(custom.TRIM_PUBLISHER.key, user, departments.SOURCING.key);

	const handlePublishClick = (e, row) => {
		e.stopPropagation();
		setSelectedPublishRow(row);
		setPublishModalMode("publish");
		setPublishModal(true);
	};

	const handleUnpublishClick = (e, row) => {
		e.stopPropagation();
		setSelectedPublishRow(row);
		setPublishModalMode("unpublish");
		setPublishModal(true);
	};

	const handlePublishSave = async (status) => {
		if (!selectedPublishRow?.trim_code) return;
		setLoadingOpen(true);
		try {
			const publishPayload = {
				...selectedPublishRow,
				status,
				published_by: user.email,
				published_date: moment().format("YYYY-MM-DD"),
				last_updated_by: user.email,
			};
			if (status === statusOptions.REJECTED.key) {
				publishPayload.trim_active_status = "INACTIVE";
			}
			const res = await axios.put(`/api/sourcing/trims_master/publish/${selectedPublishRow.trim_code}`, publishPayload);
			const updatedRow = {
				...(res?.data?.result ?? {}),
				...publishPayload,
			};
			setData((prev) =>
				prev.map((row) => {
					if (row.trim_code === selectedPublishRow.trim_code) return updatedRow;
					return row;
				}),
			);
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				severity: "success",
				message: `Trim ${status === statusOptions.PUBLISHED.key ? "Published" : "Rejected"} Successfully`,
			});
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({ ...snackBar, severity: "error", message: parseErrorMessage(error) });
		} finally {
			setLoadingOpen(false);
			setPublishModal(false);
			setSelectedPublishRow(null);
		}
	};

	const handleUnpublishSave = async () => {
		if (!selectedPublishRow?.trim_code) return;
		setLoadingOpen(true);
		try {
			const res = await axios.put(`/api/sourcing/trims_master/unpublish/${selectedPublishRow.trim_code}`);
			const updatedRow = res?.data?.result ?? {};
			setData((prev) =>
				prev.map((row) => {
					if (row.trim_code === selectedPublishRow.trim_code) return { ...row, ...updatedRow };
					return row;
				}),
			);
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				severity: "success",
				message: "Trim Unpublished Successfully",
			});
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({ ...snackBar, severity: "error", message: parseErrorMessage(error) });
		} finally {
			setLoadingOpen(false);
			setPublishModal(false);
			setSelectedPublishRow(null);
		}
	};

	const masterTableColumns = useMemo(
		() =>
			[
				trimsImageColumn,
				trimsMasterColumnsObj.trim_code,
				trimCategoryColumn,
				trimsMasterColumnsObj.item_sub_category,
				trimsMasterColumnsObj.item_description,
				trimsMasterColumnsObj.brand,
				trimsMasterColumnsObj.designer,
				...createdByColumns,
				publishColumns.published_by,
				{
					...trimsMasterColumnsObj.trim_active_status,
					renderCell: ({ row }) => statusBadgeCell(row.trim_active_status),
				},
				{
					key: "publish_status",
					name: "Publish Status",
					field: "publish_status",
					minWidth: 130,
					headerAlign: "center",
					align: "center",
					renderCell: ({ row }) => publishStatusBadgeCell(row?.status),
				},
				{
					key: "publish_action",
					name: "Publish Actions",
					field: "publish_action",
					minWidth: 140,
					headerAlign: "center",
					align: "center",
					renderCell: ({ row }) => {
						if (!canManagePublish) return "-";
						if (row?.status === TRIM_PUBLISH_STATUS.PUBLISHED) {
							return (
								<Button
									variant="outlined"
									size="small"
									color="warning"
									startIcon={<Unpublished />}
									onClick={(e) => handleUnpublishClick(e, row)}>
									Unpublish
								</Button>
							);
						}
						if (row?.status === TRIM_PUBLISH_STATUS.REJECTED) return "-";
						return (
							<Button
								variant="contained"
								size="small"
								startIcon={<Publish />}
								onClick={(e) => handlePublishClick(e, row)}
								sx={{ textTransform: "capitalize" }}>
								Publish
							</Button>
						);
					},
				},
			].map((row) => ({
				...row,
				align: row.align || "center",
				headerAlign: row.headerAlign || "center",
			})),
		[user, canManagePublish],
	);

	const supplierTableColumns = useMemo(
		() =>
			[
				trimsImageColumn,
				trimsSupplierColumnsObj.trim_supplier_code,
				trimsMasterColumnsObj.trim_code,
				trimsSupplierColumnsObj.supplier,
				trimsSupplierColumnsObj.unit_price,
				trimsSupplierColumnsObj.currency,
				trimsSupplierColumnsObj.unit,
				trimCategoryColumn,
				trimsMasterColumnsObj.item_description,
				trimsMasterColumnsObj.brand,
				trimsMasterColumnsObj.designer,
				trimsSampleColumnsObj.sample_delivery_status,
				trimsSampleColumnsObj.sample_etd_date,
				trimsSupplierColumnsObj.designer_approval,
				{
					...trimsSupplierColumnsObj.supplier_active_status,
					renderCell: ({ row }) => statusBadgeCell(row.supplier_active_status),
				},
				...createdByColumns,
			].map((row) => ({
				...row,
				align: row.align || "center",
				headerAlign: row.headerAlign || "center",
			})),
		[],
	);

	const tableColumns = viewLevel === TRIMS_VIEW_LEVELS.SUPPLIER ? supplierTableColumns : masterTableColumns;
	const rowIdKey =
		viewLevel === TRIMS_VIEW_LEVELS.SUPPLIER
			? trimsSupplierColumnsObj.trim_supplier_code.key
			: trimsMasterColumnsObj.trim_code.key;

	const fetchData = useCallback(
		async (limit, offset, filter) => {
			const isFirstLoad = data.length === 0;
			if (isFirstLoad) setLoadingOpen(true);
			try {
				const res = await axios.post(`/api/sourcing/trims_master/get`, {
					limit,
					offset,
					filter,
					view_level: viewLevel,
				});
				const result = res.data.result;
				if (result?.kpi) setKpi(result.kpi);
				return { rows: result.rows, total_rows_count: result.kpi?.total ?? 0 };
			} finally {
				if (isFirstLoad) setLoadingOpen(false);
			}
		},
		[viewLevel, data.length, setLoadingOpen],
	);

	const handleCreateClick = () => {
		if (trimViewOnly) return;
		setSelectedRow();
		setModal(true);
		setModalMode("create");
	};

	const handleFabricRowSelect = async (row) => {
		if (viewLevel === TRIMS_VIEW_LEVELS.SUPPLIER && !blockSupplierDetails) {
			setLoadingOpen(true);
			try {
				const res = await axios.post(`/api/sourcing/trims_master/get`, {
					limit: 1,
					offset: 0,
					filter: { trim_code: row.trim_code },
					view_level: TRIMS_VIEW_LEVELS.MASTER,
				});
				const fullRow = res.data?.result?.rows?.[0];
				if (!fullRow) throw new Error("Trim not found");
				setSelectedRow(fullRow);
			} catch (error) {
				setSnackBarOpen(true);
				setSnackBar({ ...snackBar, severity: "error", message: parseErrorMessage(error) });
				return;
			} finally {
				setLoadingOpen(false);
			}
		} else {
			setSelectedRow(row);
		}
		setModal(true);
		setModalMode("update");
	};

	const handleViewLevelChange = (_, nextViewLevel) => {
		if (!nextViewLevel || nextViewLevel === viewLevel) return;
		setViewLevel(nextViewLevel);
		setData([]);
		setRowCount(0);
		setKpi({});
	};

	const handleCreateorEditSave = async (apiBody) => {
		setLoadingOpen(true);
		try {
			if (modalMode === "create") {
				const createPayload = normalizeTrimStatusPayload({
					...apiBody,
					created_by: user.email,
					last_updated_by: user.email,
				});
				const res = await axios.post("/api/sourcing/trims_master/create", createPayload);
				setData((prev) => [{ ...(res?.data?.result ?? {}), ...createPayload }, ...prev]);
				setModal(false);
				setSelectedRow();
			} else {
				const updatePayload = normalizeTrimStatusPayload({
					...apiBody,
					last_updated_by: user.email,
				});
				await axios.put(`/api/sourcing/trims_master/update/${apiBody?.trim_code}`, updatePayload).then((res) => {
					const updatedRow = {
						...(res?.data?.result ?? {}),
						...updatePayload,
					};
					setData((prev) =>
						prev.map((row) => {
							if (row.trim_code === apiBody.trim_code) return updatedRow;
							return row;
						}),
					);
					setModal(false);
					setSelectedRow();
				});
			}
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				severity: "success",
				message: `Trim ${modalMode === "create" ? "Created" : "Updated"} Successfully`,
			});
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({ ...snackBar, severity: "error", message: parseErrorMessage(error) });
		} finally {
			setLoadingOpen(false);
		}
	};

	const handleDownloadClick = () => {
		setExportModal(true);
	};

	const kpiGroups =
		viewLevel === TRIMS_VIEW_LEVELS.SUPPLIER
			? [
					{
						key: "overview",
						items: [
							{ label: "Total", value: kpi.total, color: grey[800] },
							{ label: "Published", value: kpi.published, color: "#2e7d32" },
							{ label: "Created", value: kpi.created, color: "#ed6c02" },
							{ label: "Unpublished", value: kpi.unpublished, color: "#ef6c00" },
							{ label: "Active", value: kpi.active_suppliers, color: "#2e7d32" },
							{ label: "Inactive", value: kpi.inactive_suppliers, color: "#c62828" },
							{ label: "Del. Pending", value: kpi.delivery_pending, color: "#b71c1c" },
							{ label: "ETD On Track", value: kpi.etd_open, color: "#2e7d32" },
							{ label: "ETD Breach", value: kpi.etd_breach, color: "#c62828" },
						],
					},
				]
			: [
					{
						key: "overview",
						items: [
							{ label: "Total", value: kpi.total, color: grey[800] },
							{ label: "Published", value: kpi.published, color: "#2e7d32" },
							{ label: "Created", value: kpi.created, color: "#ed6c02" },
							{ label: "Unpublished", value: kpi.unpublished, color: "#ef6c00" },
							{ label: "Inactive/Rejected", value: kpi.inactive_rejected, color: "#c62828" },
							{ label: "Active", value: kpi.active, color: "#1565c0" },
						],
					},
				];

	return (
		<Box sx={{ height: "calc(100vh - 135px)", p: 1, boxSizing: "border-box", display: "flex", flexDirection: "column", gap: 1 }}>
			<Box display="flex" columnGap={1} pt={0.5} flexWrap="wrap" alignItems="center" rowGap="5px">
				<Button
					variant="contained"
					size="small"
					startIcon={<FilterAltRounded />}
					onClick={handleFilterClick}
					sx={{ textTransform: "capitalize" }}>
					Filter
				</Button>
				:
				{!filterColumns?.length && (
					<Typography variant="body1" fontSize={16} color={grey[700]}>
						None
					</Typography>
				)}
				{filterColumns?.map((row) => (
					<Chip
						key={row.key}
						label={`${row.name} : ${
							Array.isArray(filterColumnVal[row.key]) ? filterColumnVal[row.key]?.join(", ") : filterColumnVal?.[row.key]
						}`}
						size="small"
					/>
				))}
			</Box>

			<Box sx={{ flex: 1, minHeight: 0 }}>
			<TanstackDatagrid
				key={`trims-grid-${viewLevel}`}
				columns={tableColumns}
				pageData={data}
				setPageData={setData}
				rowCount={rowCount}
				setRowCount={setRowCount}
				rowHeight={72}
				filterObj={filterColumnVal}
				setFilterObj={setFilterColumnVal}
				disableColumnFilter
				disableColumnSort
				isLoading={false}
				disablePagination
				emptyMessage="No matching records"
				handleRowClick={({ row }) => handleFabricRowSelect(row)}
				getServerSideData={fetchData}
				getRowId={(row) => row[rowIdKey]}>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 1,
						py: 0.5,
						width: "100%",
						minHeight: 44,
					}}>
					<ToggleButtonGroup
						size="small"
						color="primary"
						exclusive
						value={viewLevel}
						onChange={handleViewLevelChange}
						sx={{ flexShrink: 0, "& .MuiToggleButton-root": { px: 1.5, textTransform: "none" } }}>
						{availableViewLevelOptions.map((option) => (
							<ToggleButton key={option.key} value={option.key}>
								{option.name}
							</ToggleButton>
						))}
					</ToggleButtonGroup>
					<Divider orientation="vertical" flexItem sx={{ height: 36, alignSelf: "center" }} />
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							gap: 1,
							flex: 1,
							minWidth: 0,
							overflowX: "auto",
							"&::-webkit-scrollbar": { height: 4 },
							"&::-webkit-scrollbar-thumb": { bgcolor: grey[300], borderRadius: 2 },
						}}>
						{kpiGroups.map((group, groupIndex) => (
							<Stack key={group.key} direction="row" spacing={0.75} alignItems="center" flexShrink={0}>
								{group.items.map((card) => (
									<KpiChip key={card.label} label={card.label} value={card.value} color={card.color} />
								))}
								{groupIndex < kpiGroups.length - 1 && (
									<Divider orientation="vertical" flexItem sx={{ height: 28, alignSelf: "center", mx: 0.25 }} />
								)}
							</Stack>
						))}
					</Box>
					<Divider orientation="vertical" flexItem sx={{ height: 36, alignSelf: "center" }} />
					<Stack direction="row" spacing={1} alignItems="center" flexShrink={0}>
						<Button variant="outlined" size="small" onClick={handleDownloadClick} startIcon={<DownloadRounded />}>
							Export
						</Button>
						{!trimViewOnly && (
							<Button variant="contained" size="small" onClick={handleCreateClick} startIcon={<AddRounded />}>
								Create
							</Button>
						)}
					</Stack>
				</Box>
			</TanstackDatagrid>
			</Box>

			{filterModal && (
				<FilterModal
					modal={filterModal}
					handleCloseModal={handleCloseFilterModal}
					filterColumns={filterColumns}
					setFilterColumns={setFilterColumns}
					filterColumnVal={filterColumnVal}
					setFilterColumnVal={setFilterColumnVal}
					localStorageStr={lsTrimsFilterValStr}
					allColumns={filterColumnOptions}
				/>
			)}
			{modal && (
				<>
					{modalMode === "create" ? (
						<CreateModal
							open={modal}
							setOpen={setModal}
							mode={modalMode}
							handleSaveFunction={handleCreateorEditSave}
							selectedRow={selectedRow}
							setSelectedRow={setSelectedRow}
							blockSupplierDetails={blockSupplierDetails}
						/>
					) : (
						<UpdateModal
							open={modal}
							setOpen={setModal}
							handleSaveFunction={handleCreateorEditSave}
							selectedRow={selectedRow}
							setSelectedRow={setSelectedRow}
							blockSupplierDetails={blockSupplierDetails}
							disableEdit={trimViewOnly || isTrimPublished(selectedRow)}
						/>
					)}
				</>
			)}
			{publishModal && (
				<PublishModal
					modal={publishModal}
					setModal={setPublishModal}
					mode={publishModalMode}
					selectedRow={selectedPublishRow}
					setSelectedRow={setSelectedPublishRow}
					submitFunction={handlePublishSave}
					unpublishFunction={handleUnpublishSave}
				/>
			)}
			{exportModal && (
				<DataExportModal
					modal={exportModal}
					handleCloseModal={() => setExportModal(false)}
					filterColumnVal={filterColumnVal}
					blockSupplierDetails={blockSupplierDetails}
				/>
			)}
		</Box>
	);
};

export default TrimsMaster;
